import { Subscription } from '@rails/actioncable';
import {
  type DriverDay_Read,
  getV1CompaniesCompanyIdDriverDays,
  type GetV1CompaniesCompanyIdDriverDaysData,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdJobs,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdPhasesTypeahead,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdSignatures,
  getV1CompaniesCompanyIdDriverDaysId,
  type Job_Read,
  ModelError_Response,
  patchV1CompaniesCompanyIdDriverDaysId,
  putV1CompaniesCompanyIdDriverDaysDriverDayIdApprove,
  putV1CompaniesCompanyIdDriverDaysDriverDayIdUnapprove,
  type ShiftAdjustmentType,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { EXPORT_ROW_LIMIT } from '~constants/consts';
import { DriverDayQueryOrdersSort } from '~pages/Approvals/DriverDay/constants';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store/RootStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { downloadCSV } from '~utils/csv/csvProcessor';
import { transformDriverDaysToCSV } from '~utils/csv/driverDayCsvExport';
import { exportRowLimitAlert } from '~utils/csv/exportRowLimitAlert';
import { fetchAllApiPages, TooManyRecordsError } from '~utils/fetchAllPages';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Job } from '../useJob/models';
import { PhaseBasic } from '../useProjects/models';
import { DriverDaySignature, SignatureFactory } from '../useSignatures/models';
import { DriverDay } from './models';

type DriverDaysQuery = NonNullable<GetV1CompaniesCompanyIdDriverDaysData['query']>;

interface GetDriverDayPhasesTypeaheadProps {
  driverDayId: string;
}

interface FetchDriverDaysByCompanyIdProps {
  companyId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
  sort?: OrderOption<DriverDayQueryOrdersSort>;
}

export const useDriverDays = () => {
  const { driverDayStore, toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // Monotonic token shared by fetches and probes: bumped and captured when a
  // main fetch starts, pinned to the probe that fetch spawns, checked before
  // the probe writes. Capturing at fetch start (not probe start) matters: a
  // superseded fetch that resolves late would otherwise spawn a probe that
  // adopts the newest generation and writes hasUnfilteredResults for a stale
  // search (TRE-15529).
  const probeGenerationRef = useRef(0);

  // Probe = the exact query the grid would issue right after clearing filters
  // (search only, no dates/entity filters), capped at one row. Powers the
  // "Clear filters and search again" hint. Failures stay silent (matches the
  // Dispatch probe): the hint simply doesn't show.
  const checkUnfilteredDriverDays = useCallback(
    async (companyId: string, searchQuery: string, generation: number) => {
      try {
        const response = await getV1CompaniesCompanyIdDriverDays({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': 1,
            'search[datagrid]': searchQuery,
          },
        });

        if (generation !== probeGenerationRef.current) {
          return;
        }

        driverDayStore.setHasUnfilteredResults(response.data.data.length > 0);
      } catch {
        if (generation === probeGenerationRef.current) {
          driverDayStore.setHasUnfilteredResults(false);
        }
      }
    },
    [driverDayStore],
  );

  const [isLoadingDriverDayJobs, setIsLoadingDriverDayJobs] = useState(false);
  const [isLoadingDriverDayPhases, setIsLoadingDriverDayPhases] = useState(false);
  const [isUpdatingDriverDay, setIsUpdatingDriverDay] = useState(false);

  const [jobSubscription, setJobSubscription] = useState<Subscription>();

  const subscribeToDriverDayUpdates = useCallback(
    (
      companyId: string,
      onDriverDayUpdate: (driverDay: DriverDay) => void,
      options?: { triggerResourceUsageLogsRefetch?: boolean },
    ) =>
      connection.realTimeConnection?.subscriptions.create(
        {
          channel: realTimeChannels.DriverDayUpdateChannel,
          company_id: companyId,
          user_id: userStore.user.id,
        },
        {
          received: (response: { data: DriverDay_Read }) => {
            const driverDay = DriverDay.parse(response.data);
            onDriverDayUpdate(driverDay);
            if (options?.triggerResourceUsageLogsRefetch) {
              driverDayStore.triggerDriverDayRTURefetch(driverDay.id);
            }
          },
        },
      ),
    [driverDayStore, userStore.user.id],
  );

  const subscribeToJobUpdates = useCallback(
    (companyId: string, options?: { triggerResourceUsageLogsRefetch?: boolean }) => {
      subscribeToChannel(
        realTimeChannels.CompanyJobUpdateChannel,
        companyId,
        setJobSubscription,
        (response: { data: Job_Read }) => {
          const job = Job.parse(response?.data);
          driverDayStore.setJobByJobId(job.id, job);

          // Clear this job from waiting for RTU when we receive an update
          // Add a small delay to allow UI to update with new state while button is still disabled
          setTimeout(() => {
            driverDayStore.clearJobsWaitingForRTU([job.jobId]);
          }, 500);

          if (options?.triggerResourceUsageLogsRefetch) {
            driverDayStore.triggerJobRTURefetch(job.id);
          }
        },
      );
    },
    [setJobSubscription, driverDayStore],
  );

  const buildDriverDaysFilterParams = (params: DriverDaysQuery) => {
    if (driverDayStore.filters.customerAccountIds) {
      params['filter[customer_account_ids]'] = Array.from(
        driverDayStore.filters.customerAccountIds,
      );
    }
    if (driverDayStore.filters.driverIds) {
      params['filter[driver_ids]'] = Array.from(driverDayStore.filters.driverIds);
    }
    if (driverDayStore.filters.vendorAccountIds) {
      params['filter[vendor_account_ids]'] = Array.from(
        driverDayStore.filters.vendorAccountIds,
      );
    }
    if (driverDayStore.dateFilters.startDate) {
      params['filter[start_date]'] = driverDayStore.dateFilters.startDate.toISOString();
      // To account for a single date selection
      if (!driverDayStore.dateFilters.endDate) {
        params['filter[end_date]'] = driverDayStore.dateFilters.startDate.toISOString();
      }
    }
    if (driverDayStore.dateFilters.endDate) {
      params['filter[end_date]'] = driverDayStore.dateFilters.endDate.toISOString();
    }
  };

  const buildDriverDaysQuery = ({
    cursor,
    limit,
    link,
    searchQuery,
    sort,
  }: Omit<FetchDriverDaysByCompanyIdProps, 'companyId' | 'signal'>) => {
    const params: DriverDaysQuery = {
      'page[limit]': limit,
    };

    if (link && cursor) {
      params[`page[${link}]`] = cursor;
    }

    buildDriverDaysFilterParams(params);

    if (searchQuery) {
      params['search[datagrid]'] = searchQuery;
    }

    // Add sort parameters
    if (sort) {
      const { orderBy, sortBy } = sort;
      switch (orderBy) {
        case DriverDayQueryOrdersSort.DRIVER_DAY_DATE:
          params['sort[driver_day_date]'] = sortBy;
          break;
        case DriverDayQueryOrdersSort.DRIVER_NAME:
          // Apply both first and last name sorting for combined driver name sort
          params['sort[driver][first_name]'] = sortBy;
          params['sort[driver][last_name]'] = sortBy;
          break;
        case DriverDayQueryOrdersSort.VENDOR_ACCOUNT_NAME:
          params['sort[vendor_account][name]'] = sortBy;
          break;
      }
    }

    return params;
  };

  const fetchDriverDaysByCompanyId = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      searchQuery,
      signal,
      sort,
    }: FetchDriverDaysByCompanyIdProps) => {
      // Any new fetch invalidates the previous probe answer, any in-flight
      // probe, and any probe a superseded fetch spawns after this point.
      probeGenerationRef.current += 1;
      const fetchGeneration = probeGenerationRef.current;
      driverDayStore.setHasUnfilteredResults(false);

      const query = buildDriverDaysQuery({ cursor, limit, link, searchQuery, sort });

      const response = await getV1CompaniesCompanyIdDriverDays({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const data = response.data.data;

      const shouldProbeForUnfilteredResults =
        !link &&
        data.length === 0 &&
        Boolean(searchQuery?.trim()) &&
        driverDayStore.hasActiveNonSearchFilters;

      if (shouldProbeForUnfilteredResults) {
        void checkUnfilteredDriverDays(companyId, searchQuery ?? '', fetchGeneration);
      }

      return {
        data: data.map((dDayJson) => DriverDay.parse(dDayJson)),
        pagination: extractPagination(response),
      };
    },
    [checkUnfilteredDriverDays],
  );

  const fetchJobsForDriverDay = useCallback(
    async (driverDayId: string, silent?: boolean) => {
      !silent && setIsLoadingDriverDayJobs(true);
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const jobsResponse = await getV1CompaniesCompanyIdDriverDaysDriverDayIdJobs({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        });
        const jobsData = jobsResponse.data.data;
        const jobs = jobsData.map((jobJson) => Job.parse(jobJson));
        driverDayStore.setJobIdsByDriverDayId(
          driverDayId,
          jobs.map((job) => job.id),
        );
        driverDayStore.setJobs(jobs);
      } catch (err) {
        connection.handleRequestError(
          err,
          t('error_messages.driver_days.failed_to_fetch_driver_day_jobs'),
        );
      } finally {
        !silent && setIsLoadingDriverDayJobs(false);
      }
    },
    [driverDayStore, setIsLoadingDriverDayJobs, userStore],
  );

  const updateDriverDay = useCallback(
    async (
      id: string,
      adjustmentMinutes: number,
      externalId: string | null,
      adjustedStartedShiftAt: string | null,
      adjustedEndedShiftAt: string | null,
      adjustmentType?: ShiftAdjustmentType,
    ) => {
      setIsUpdatingDriverDay(true);
      try {
        const response = await patchV1CompaniesCompanyIdDriverDaysId({
          path: { 'company-id': companyId, id },
          body: {
            external_id: externalId,
            shift_adjustment_minutes: adjustmentMinutes,
            shift_adjustment_type: adjustmentType,
            adjusted_ended_shift_at: adjustedEndedShiftAt,
            adjusted_started_shift_at: adjustedStartedShiftAt,
          },
        });

        return { driverDay: DriverDay.parse(response.data.data) };
      } catch (error) {
        const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

        if (isUnprocessableEntityError) {
          const typedError = error as AxiosError<ModelError_Response>;

          return { errors: typedError.response?.data.error.errors ?? [] };
        }

        connection.handleRequestError(
          error,
          t('error_messages.driver_days.failed_to_update_driver_day'),
        );
        throw error;
      } finally {
        setIsUpdatingDriverDay(false);
      }
    },
    [companyId, setIsUpdatingDriverDay],
  );

  const approveDriverDay = useCallback(
    async (companyId: string, driverDayId: string) => {
      setIsUpdatingDriverDay(true);
      try {
        const response = await putV1CompaniesCompanyIdDriverDaysDriverDayIdApprove({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        });

        return DriverDay.parse(response.data.data);
      } catch (err) {
        connection.handleRequestError(
          err,
          t('error_messages.driver_days.failed_to_approve_driver_day'),
        );
        throw err;
      } finally {
        setIsUpdatingDriverDay(false);
      }
    },
    [setIsUpdatingDriverDay],
  );

  const unapproveDriverDay = useCallback(
    async (companyId: string, driverDayId: string) => {
      setIsUpdatingDriverDay(true);
      try {
        const response = await putV1CompaniesCompanyIdDriverDaysDriverDayIdUnapprove({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        });

        return DriverDay.parse(response.data.data);
      } catch (err) {
        connection.handleRequestError(
          err,
          t('error_messages.driver_days.failed_to_unapprove_driver_day'),
        );
        throw err;
      } finally {
        setIsUpdatingDriverDay(false);
      }
    },
    [setIsUpdatingDriverDay],
  );

  const getDriverDayPhasesTypeahead = async (
    options: GetDriverDayPhasesTypeaheadProps,
  ) => {
    setIsLoadingDriverDayPhases(true);

    try {
      const phases = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdDriverDaysDriverDayIdPhasesTypeahead({
            path: {
              'company-id': getEffectiveUserCompanyId(userStore),
              'driver-day-id': options.driverDayId,
            },
            query,
          }),
        PhaseBasic.parse,
      );

      driverDayStore.setPhaseTypeheadsByDriverDayId(options.driverDayId, phases);
    } catch (err) {
      connection.handleRequestError(
        err,
        t('error_messages.driver_days.failed_to_fetch_phases'),
      );
    } finally {
      setIsLoadingDriverDayPhases(false);
    }
  };

  const getDriverDayById = async (driverDayId: string, signal?: AbortSignal) => {
    try {
      const response = await getV1CompaniesCompanyIdDriverDaysId({
        path: { 'company-id': companyId, id: driverDayId },
        signal,
      });
      const driverDay = DriverDay.parse(response.data.data);
      return driverDay;
    } catch (err) {
      if (signal?.aborted) {
        throw err;
      }

      connection.handleRequestError(
        err,
        t('error_messages.driver_days.failed_to_fetch_driver_day'),
      );
      throw err;
    }
  };

  const getDriverDaySignatures = useCallback(
    async (driverDayId: string): Promise<DriverDaySignature[]> => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const response = await getV1CompaniesCompanyIdDriverDaysDriverDayIdSignatures({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        });
        return response.data.data.map((item) => SignatureFactory.create(item));
      } catch (err) {
        connection.handleRequestError(
          err,
          t('error_messages.driver_days.failed_to_fetch_driver_day_signatures'),
        );
        return [];
      }
    },
    [userStore],
  );

  const exportDriverDaysCsv = useCallback(async () => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const filterQuery: DriverDaysQuery = {};

      buildDriverDaysFilterParams(filterQuery);

      const allDriverDays = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdDriverDays({
            path: { 'company-id': companyId },
            query: { ...filterQuery, ...query },
          }),
        DriverDay.parse,
        { maxRecords: EXPORT_ROW_LIMIT },
      );

      if (allDriverDays.length === 0) {
        toasterStore.push(alert(t('approvals.export.no_data'), AlertTypes.info));
        return;
      }

      const csvData = transformDriverDaysToCSV(allDriverDays);
      const dateForFilename = dayjs().format('YYYY-MM-DD');
      const filename = `${userStore.userCompany.legalName}_approve_work_${dateForFilename}.csv`;
      downloadCSV(csvData, filename);

      toasterStore.push(
        alert(
          t('approvals.export.success', { count: allDriverDays.length }),
          AlertTypes.success,
        ),
      );
    } catch (error) {
      if (error instanceof TooManyRecordsError) {
        toasterStore.push(exportRowLimitAlert());

        return;
      }

      connection.handleRequestError(
        error,
        t('error_messages.driver_days.failed_to_export_csv'),
      );
    }
  }, [driverDayStore, toasterStore, userStore]);

  return {
    approveDriverDay,
    exportDriverDaysCsv,
    fetchDriverDaysByCompanyId,
    fetchJobsForDriverDay,
    getDriverDayPhasesTypeahead,
    getDriverDaySignatures,
    isLoadingDriverDayJobs,
    isUpdatingDriverDay,
    isLoadingDriverDayPhases,
    subscribeToDriverDayUpdates,
    subscribeToJobUpdates,
    jobSubscription,
    unapproveDriverDay,
    updateDriverDay,
    getDriverDayById,
  };
};
